"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Search, ArrowRight, CircleCheck as CheckCircle2, Target, ChartBar as BarChart3, Compass } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const bedfordStats = [{
  id: "stat-1",
  label: "Monthly Local Searches",
  value: "145,000+",
  change: "+18% YoY Growth",
  description: "High-intent queries for local services in Bedfordshire"
}, {
  id: "stat-2",
  label: "Mobile Search Share",
  value: "68%",
  change: "Immediate Intent",
  description: "Bedford residents searching for local solutions on mobile"
}, {
  id: "stat-3",
  label: "Unclaimed Local SERPs",
  value: "42%",
  change: "High Opportunity",
  description: "First-page search positions currently held by weak competitors"
}, {
  id: "stat-4",
  label: "AI Search Uptake",
  value: "3.2x",
  change: "Rapid Adoption",
  description: "Growth in Google AI Overviews for Bedford service queries"
}];
const marketOpportunities = [{
  id: "opp-1",
  title: "Professional & B2B Services",
  sector: "Corporate & Consulting",
  searchVolume: "High Commercial Intent",
  insight: "When Bedford business leaders look for commercial legal advice, corporate accounting, or IT support, they search for verified local specialists rather than national directories.",
  strategy: "I create dedicated service-and-location landing pages with structured schema markup, demonstrating service relevance to Google's ranking algorithm."
}, {
  id: "opp-2",
  title: "Local Trades & Property Services",
  sector: "Construction & Home Services",
  searchVolume: "High Transactional Intent",
  insight: "Homeowners across the MK40–MK45 postcodes experience immediate needs. When they search, Google prioritises the 3-Pack map results above standard organic websites.",
  strategy: "I optimise Google Business Profiles and refine primary category associations so you capture top-of-page map pack visibility when local demand is highest."
}, {
  id: "opp-3",
  title: "Healthcare, Clinics & Regional Retail",
  sector: "Healthcare & Consumer",
  searchVolume: "High Conversion Proximity",
  insight: "Local customers cross-check proximity, customer review sentiment, and operational details before committing to an appointment or making an in-store visit.",
  strategy: "I align your local citation profile and on-page technical factors to reinforce physical prominence and trust signals across Bedford."
}];
const searchBehaviors = [{
  id: "beh-1",
  title: "Google Map Pack Click Share",
  percentage: 68,
  details: "The top 3 map listings capture the vast majority of mobile clicks before users ever scroll to traditional organic listings."
}, {
  id: "beh-2",
  title: "High-Intent Commercial Searches",
  percentage: 76,
  details: "Searches combining specific services with 'Bedford' or nearby villages carry urgent intent and convert at significantly higher rates."
}, {
  id: "beh-3",
  title: "Review & Proximity Verification",
  percentage: 84,
  details: "Prospective customers evaluate review velocity, geographic distance, and local relevance before initiating contact."
}];
export default function Bedfordmarketanalysis() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="bedfordmarketanalysis" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto space-y-16">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2">
            <Badge variant="secondary" className="bg-secondary text-secondary-foreground border border-accent/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              <MapPin className="size-3.5 text-accent" />
              Local Search Insights
            </Badge>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            Why Bedford Businesses Need <span className="text-primary underline decoration-accent/40 underline-offset-8">Local SEO</span>
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed pt-2">
            As a local SEO consultant Bedford businesses rely on for clear search strategy, I explain the mechanics simply: Google divides local searches between standard organic blue links and the prominent Google Maps Map Pack. Because searches combining specific services with Bedford carry direct purchasing intent, your competitors are already winning customers simply by maintaining stronger service and location signals.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {bedfordStats.map((stat, i) => <motion.div key={stat.id} variants={itemVariants} data-index={i}>
              <Card className="h-full border border-border/80 bg-card hover:border-accent/40 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-1 h-full bg-primary group-hover:bg-accent transition-colors duration-300" />
                <CardHeader className="pb-2 pt-6 px-6">
                  <Badge variant="outline" className="w-fit text-xs font-medium border-accent/30 text-foreground bg-accent/10 mb-2">
                    {stat.change}
                  </Badge>
                  <CardTitle className="text-3xl sm:text-4xl font-bold text-primary font-serif">
                    {stat.value}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-6 pb-6 space-y-1">
                  <p className="font-medium text-foreground text-sm">
                    {stat.label}
                  </p>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Target className="size-5 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground font-serif">
                  High-Growth Bedford Sectors
                </h3>
                <p className="text-xs text-muted-foreground">
                  How service relevance and structured landing pages generate local enquiries
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {marketOpportunities.map((opp, i) => <Card key={opp.id} data-index={i} className="border border-border/80 bg-card hover:shadow-md transition-[box-shadow,border-color] duration-300">
                  <CardHeader className="p-6 pb-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <CardTitle className="text-lg font-semibold text-primary font-serif">
                        {opp.title}
                      </CardTitle>
                      <Badge variant="secondary" className="bg-muted text-muted-foreground text-xs font-normal">
                        {opp.sector}
                      </Badge>
                    </div>
                    <CardDescription className="text-xs font-medium text-accent mt-1 flex items-center gap-1.5">
                      <BarChart3 className="size-3.5" />
                      {opp.searchVolume}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-6 pb-6 space-y-3">
                    <p className="text-sm text-foreground/90 leading-relaxed">
                      {opp.insight}
                    </p>
                    <div className="p-3 rounded-md bg-muted/60 border border-border/60 flex items-start gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span><strong className="text-foreground">My Approach:</strong> {opp.strategy}</span>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-accent/10 text-accent">
                <Compass className="size-5 text-accent" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground font-serif">
                  Bedford Customer Search Behaviour
                </h3>
                <p className="text-xs text-muted-foreground">
                  The pathway from local query to commercial contact
                </p>
              </div>
            </div>

            <Card className="border border-border/80 bg-card p-6 space-y-6">
              <div className="space-y-6">
                {searchBehaviors.map((item, i) => <div key={item.id} data-index={i} className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-foreground">{item.title}</span>
                      <span className="font-bold text-primary font-mono text-xs">{item.percentage}%</span>
                    </div>
                    
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: `${item.percentage}%`
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.8,
                    ease: "easeOut",
                    delay: i * 0.1
                  }} className="h-full bg-gradient-to-r from-primary to-accent rounded-full" />
                    </div>
                    
                    <p className="text-xs text-muted-foreground leading-normal pt-1">
                      {item.details}
                    </p>
                  </div>)}
              </div>

              <Separator className="bg-border" />

              <div className="bg-secondary/50 border border-accent/20 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                  <Search className="size-3.5 text-accent" />
                  <span>Personal Consultant Insight</span>
                </div>
                <p className="text-xs text-foreground/90 italic leading-relaxed">
                  {"\"When executing local search optimisation Bedford campaigns, I focus on the three pillars Google uses for local ranking: relevance, distance, and prominence. Simply putting 'Bedford' in your footer does not suffice; you need deep topical clarity and consistent entity signals.\""}
                </p>
                <p className="text-xs font-medium text-primary text-right font-serif">
                  — Yassine Essaadi
                </p>
              </div>
            </Card>
          </div>

        </div>

        <div className="rounded-2xl bg-gradient-to-br from-primary/5 via-background to-accent/10 border border-accent/30 p-8 sm:p-10 lg:p-12 relative overflow-hidden shadow-sm">
          <div className="max-w-3xl space-y-4 relative z-10">
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 text-xs font-medium px-3 py-1">
              Direct Consultant Access
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-serif">
              Ready to Win High-Intent Search Traffic in Bedford?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              When you work with me, you collaborate directly with the SEO consultant managing your rankings. I help your business secure top visibility across both Google Maps and organic local search with no agency overhead or automated fluff.
            </p>
            <div className="pt-4 flex flex-wrap gap-4 items-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent/10 hover:text-foreground">
                <Link to="/LocalSEOPage">
                  Explore My Local SEO Services
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}